import { memo } from "react";
import {
  Eye,
  Edit2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  User,
  CreditCard,
} from "lucide-react";
import { imageOriginal } from "@repo/api-client";
import { HotelListItem } from "@repo/types";

interface HotelTableItemProps {
  hotel: HotelListItem;
  onViewDetails: (hotel: HotelListItem) => void;
  onEdit: (hotel: HotelListItem) => void;
  view?: "table" | "card";
}

const formatDate = (dateString?: string) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInitial = (name?: string) => {
  return name?.charAt(0).toUpperCase() ?? "?";
};

export const HotelTableItemRow = memo(
  ({ hotel, onViewDetails, onEdit, view = "table" }: HotelTableItemProps) => {
    if (view === "card") {
      return (
        <div className="bg-white dark:bg-[#111c44] rounded-2xl p-5 shadow-sm border border-slate-200 dark:border-white/10 hover:border-blue-200 dark:hover:border-blue-700 transition-all duration-300 group">
          <div className="flex items-start justify-between mb-5">
            <div className="flex gap-3">
              <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-[#1b254b] border border-slate-200 dark:border-white/5 shrink-0 flex items-center justify-center text-slate-500 dark:text-slate-400 font-bold overflow-hidden shadow-sm">
                {hotel.LogoUrl ? (
                  <img
                    src={imageOriginal(hotel.LogoUrl)}
                    alt={hotel.HotelName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-lg">{getInitial(hotel.HotelName)}</span>
                )}
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {hotel.HotelName}
                </h3>
                <div className="mt-1">
                  {hotel.SalesmanCode && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-[#1b254b] px-2 py-0.5 rounded-md uppercase tracking-wide border border-slate-200 dark:border-white/5">
                      <User size={10} />
                      {hotel.SalesmanCode}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide ${
                hotel.IsActive
                  ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800"
                  : "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-800"
              }`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full ${hotel.IsActive ? "bg-emerald-500" : "bg-rose-500"}`}
              />
              {hotel.IsActive ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="space-y-3 mb-5">
            <div className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-400 font-medium">
              <MapPin
                size={16}
                className="text-slate-400 dark:text-slate-500 mt-0.5 shrink-0"
              />
              <span className="line-clamp-2">
                {hotel.Address || `${hotel.City}, ${hotel.State}`}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100 dark:border-white/10">
              <div className="space-y-2.5">
                {hotel.Phone && (
                  <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
                    <Phone
                      size={14}
                      className="text-slate-400 dark:text-slate-500"
                    />
                    {hotel.Phone}
                  </div>
                )}
                {hotel.Email && (
                  <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
                    <Mail
                      size={14}
                      className="text-slate-400 dark:text-slate-500"
                    />
                    <span
                      className="truncate max-w-[120px]"
                      title={hotel.Email}
                    >
                      {hotel.Email}
                    </span>
                  </div>
                )}
              </div>
              <div className="space-y-2.5">
                {hotel.ProExpireDate && (
                  <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
                    <Calendar
                      size={14}
                      className="text-slate-400 dark:text-slate-500"
                    />
                    <span>Exp: {formatDate(hotel.ProExpireDate)}</span>
                  </div>
                )}
                {hotel.GST && (
                  <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
                    <CreditCard
                      size={14}
                      className="text-slate-400 dark:text-slate-500"
                    />
                    <span>GST: {hotel.GST}%</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onViewDetails(hotel)}
              className="flex-1 h-10 bg-white dark:bg-[#1b254b] border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-[#111c44] text-slate-700 dark:text-slate-200 rounded-xl flex items-center justify-center gap-2 text-xs font-semibold transition-all shadow-sm"
            >
              <Eye size={16} />
              Details
            </button>
            <button
              onClick={() => onEdit(hotel)}
              className="flex-1 h-10 bg-white dark:bg-[#1b254b] border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-[#111c44] text-slate-700 dark:text-slate-200 rounded-xl flex items-center justify-center gap-2 text-xs font-semibold transition-all shadow-sm"
            >
              <Edit2 size={16} />
              Edit
            </button>
          </div>
        </div>
      );
    }

    return (
      <tr className="hover:bg-slate-50/50 dark:hover:bg-[#1b254b]/30 transition-colors group border-b border-slate-100 dark:border-white/10 last:border-none">
        {/* Identity */}
        <td className="px-6 py-4 align-middle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-[#1b254b] border border-slate-200 dark:border-white/5 shrink-0 flex items-center justify-center text-slate-500 dark:text-slate-400 font-bold overflow-hidden">
              {hotel.LogoUrl ? (
                <img
                  src={imageOriginal(hotel.LogoUrl)}
                  alt={hotel.HotelName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm tracking-widest uppercase">
                  {getInitial(hotel.HotelName)}
                </span>
              )}
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-tight mb-0.5">
                {hotel.HotelName}
              </p>
              <div className="flex flex-col gap-1">
                {hotel.SalesmanCode && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-[#1b254b] px-2 py-0.5 border border-slate-200 dark:border-white/5 rounded-md w-fit">
                    <User size={10} />
                    {hotel.SalesmanCode}
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>

        {/* Location */}
        <td className="px-6 py-4 align-middle">
          <div className="flex flex-col gap-1">
            <div className="flex items-start gap-1.5 text-sm text-slate-700 dark:text-slate-300 font-medium leading-snug">
              <MapPin
                size={14}
                className="text-slate-400 dark:text-slate-500 mt-0.5 shrink-0"
              />
              <span className="max-w-[200px] line-clamp-2">
                {hotel.Address || `${hotel.City}, ${hotel.State}`}
              </span>
            </div>
            {(hotel.City || hotel.State) && (
              <div className="pl-5 text-xs text-slate-500 dark:text-slate-400 font-medium">
                {hotel.City}, {hotel.State}
              </div>
            )}
          </div>
        </td>

        {/* Contact Info */}
        <td className="px-6 py-4 align-middle">
          <div className="space-y-1.5">
            {hotel.Phone && (
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium group/item hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer">
                <Phone
                  size={13}
                  className="text-slate-400 dark:text-slate-500"
                />
                {hotel.Phone}
              </div>
            )}
            {hotel.Email && (
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium group/item hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer">
                <Mail
                  size={13}
                  className="text-slate-400 dark:text-slate-500"
                />
                <span className="truncate max-w-[150px]" title={hotel.Email}>
                  {hotel.Email}
                </span>
              </div>
            )}
          </div>
        </td>

        {/* Status & Dates */}
        <td className="px-6 py-4 align-middle">
          <div className="flex flex-col gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide w-fit ${
                hotel.IsActive
                  ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800"
                  : "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-800"
              }`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full ${hotel.IsActive ? "bg-emerald-500" : "bg-rose-500"}`}
              />
              {hotel.IsActive ? "Active" : "Inactive"}
            </span>

            <div className="space-y-1">
              {hotel.ProExpireDate && (
                <div
                  className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium"
                  title="Pro Expiry Date"
                >
                  <Calendar
                    size={13}
                    className="text-slate-400 dark:text-slate-500"
                  />
                  <span>Exp: {formatDate(hotel.ProExpireDate)}</span>
                </div>
              )}
              {hotel.GST && (
                <div
                  className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium"
                  title="GST Percentage"
                >
                  <CreditCard
                    size={13}
                    className="text-slate-400 dark:text-slate-500"
                  />
                  <span>GST: {hotel.GST}%</span>
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Actions */}
        <td className="px-6 py-4 align-middle text-center">
          <div className="flex justify-center gap-2 transition-opacity">
            <button
              onClick={() => onViewDetails(hotel)}
              className="p-2 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
              title="View Profile"
            >
              <Eye size={18} />
            </button>
            <button
              onClick={() => onEdit(hotel)}
              className="p-2 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
              title="Edit Hotel"
            >
              <Edit2 size={18} />
            </button>
          </div>
        </td>
      </tr>
    );
  },
);
